'use client'

import type { MarketId } from '@morpho-org/blue-sdk'

import {
  Button,
  Input,
  Label,
  MultiSelectClearButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text
} from '@repo/ui'
import { Search } from '@repo/ui/icons'
import { abbreviateAddress } from '@repo/utils'
import { useId, useRef, useState } from 'react'

import { isMarketId } from '@/lib/shared/market-id'

interface MarketIdLookupProps {
  marketId: MarketId | undefined
  onChange: (marketId: MarketId | undefined) => void
}

/**
 * Escape hatch for markets the chain-wide sweep never reaches — see `MARKET_QUERY` in
 * `borrowers.data.ts`. Every chain.
 *
 * Submitting an ID *replaces* the row set with a different server query rather than narrowing the
 * loaded rows, which is why this is not a `MultiSelect` and why the active ID is rendered on the
 * trigger: the market filter is hidden in targeted mode, so the trigger is the only place a reader
 * can tell which of the two row sets is on screen.
 */
export function MarketIdLookup({ marketId, onChange }: MarketIdLookupProps) {
  const labelId = useId()
  const inputId = useId()
  const errorId = useId()
  const helpId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)

  function handleOpenChange(next: boolean) {
    setOpen(next)
    // Seeded on open rather than in an effect, so the field holds all 66 characters by the first
    // paint and an abandoned draft never outlives a close.
    if (next) {
      setDraft(marketId ?? '')
      setError(null)
    }
  }

  function handleClear() {
    setOpen(false)
    setDraft('')
    setError(null)
    onChange(undefined)
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()

    const trimmed = draft.trim()

    if (!trimmed) {
      setError('Enter a market ID')
      return
    }

    if (!isMarketId(trimmed)) {
      setError('Market ID must be a 66-character hex string starting with 0x')
      return
    }

    setError(null)
    setOpen(false)
    onChange(trimmed)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        {/* No `role="combobox"` and no `tooltip`, both unlike `MultiSelect`: this popover holds a
            form rather than a listbox, and a string `tooltip` would overwrite `aria-label`. */}
        <Button
          aria-label={marketId ? `Market ID ${abbreviateAddress(marketId)}` : 'Market ID'}
          variant="ghost"
          size="sm"
          className="text-secondary-foreground"
        >
          <Search className="size-5" />
          <Text tag="span" size="3xs" className="gap-2xs flex items-center">
            Market ID
            {marketId && <span className="font-mono">{abbreviateAddress(marketId)}</span>}
          </Text>
          {marketId && <MultiSelectClearButton label="market ID" onClear={handleClear} />}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        aria-labelledby={labelId}
        className="w-[min(24rem,calc(100vw-2rem))]"
        onOpenAutoFocus={event => {
          // Radix parks focus on the content wrapper. Aim it at the field instead, pre-selected so
          // a paste replaces the active ID in one action.
          event.preventDefault()
          inputRef.current?.focus()
          inputRef.current?.select()
        }}
      >
        <form onSubmit={handleSubmit} className="space-y-2">
          <Label
            id={labelId}
            htmlFor={inputId}
            className="text-secondary-foreground text-xs font-normal"
          >
            Market ID
          </Label>

          <Input
            ref={inputRef}
            id={inputId}
            size="sm"
            placeholder="0x..."
            className="font-mono"
            value={draft}
            onChange={event => {
              setDraft(event.target.value)
              setError(null)
            }}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? `${errorId} ${helpId}` : helpId}
          />

          {error && (
            <Text id={errorId} size="2xs" className="text-destructive-foreground">
              {error}
            </Text>
          )}

          <Text id={helpId} size="2xs" className="text-secondary-foreground">
            Automatic discovery covers markets listed on Morpho. Paste an ID to check any market.
          </Text>

          <div className="flex items-center gap-2 pt-1">
            {marketId && (
              <Button type="button" variant="ghost" size="sm" onClick={handleClear}>
                Clear
              </Button>
            )}
            <Button type="submit" size="sm" className="ml-auto">
              Check market
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  )
}
